import { Map, MapBrowserEvent } from "ol";
import { Coordinate } from "ol/coordinate";
import { EventsKey } from "ol/events";
import { FeatureLike } from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import { Vector as VectorLayer } from "ol/layer";
import { unByKey } from "ol/Observable";
import { useCallback, useRef, useState } from "react";
import { findLayerByFeatureId } from "../functions/findLayerByFeatureId";
import { MapItem, PortMapItem } from "../types/mapItems";
import { usePortFocus } from "./usePortFocus";

type PopupTypes = {
  click: boolean;
  hover: boolean;
};

const AllowedTypes: Record<MapItem["type"], PopupTypes> = {
  vessel: { click: true, hover: true },
  wind: { click: true, hover: true },
  sea: { click: true, hover: true },
  wave: { click: true, hover: true },
  condition: { click: true, hover: true },
  event: { click: true, hover: true },
  port: { click: true, hover: false },
  fairway: { click: true, hover: true },
  measurement: { click: true, hover: false },
  dam: { click: true, hover: true },
  ferry: { click: true, hover: true },
  berth: { click: true, hover: true },
  aton: { click: true, hover: true },
  "nautical-warning": { click: true, hover: true },
  "port-service": { click: true, hover: true },
  "yepzon-tracker": {
    click: false,
    hover: false,
  },
  "taival-tracker": {
    click: false,
    hover: false,
  },
  "ptop-car": {
    click: true,
    hover: true,
  },
  "ptop-trailer": {
    click: true,
    hover: true,
  },
  "ais-vessel-history-arrow": {
    hover: true,
    click: false,
  },
  "project-shape-progress": {
    hover: false,
    click: true,
  },
  "project-shape-plan": {
    hover: false,
    click: true,
  },
  "poi-item": {
    hover: false,
    click: true,
  },
  "arrival-zone": {
    hover: true,
    click: true,
  },
  equipment: {
    hover: true,
    click: true,
  },
};

interface MapPopupArgs {
  map?: Map;
}

type DisablePopups = Partial<Record<MapItem["type"], DisableOptions>>;

interface DisableOptions {
  disableClick: boolean;
  disableHover: boolean;
}

interface UseMapPopupReturn {
  createPopup: (opt: MapPopupArgs) => void;
  selectedItem?: MapItem;
  hoveredItem?: MapItem;
  multipleItems?: MapItem[];
  unmountPopup: () => void;
}

export type UseMapPopupProps = {
  disabledPopups?: DisablePopups;
  disablePopups?: boolean;
};

function useMapPopup(
  { disabledPopups, disablePopups }: UseMapPopupProps = {
    disablePopups: false,
  },
): UseMapPopupReturn {
  const { focusedPort } = usePortFocus({});
  const [hoveredItem, setHoveredItem] = useState<MapItem>();
  const [selectedItem, setSelectedItem] = useState<MapItem>();
  const [multipleItems, setMultipleItems] = useState<MapItem[]>();

  const clickListenerKey = useRef<EventsKey>();
  const hoverListenerKey = useRef<EventsKey>();

  const createPopup = useCallback(
    ({ map }: MapPopupArgs): void => {
      if (!map) return;
      const listener = (e: MapBrowserEvent<UIEvent>) => {
        const overlay = map.getOverlayById("popup");
        if (!overlay) return;
        let item:
          | (MapItem & {
              geometry: Geometry;
            })
          | undefined;
        let itemCoords: Coordinate | undefined;
        let setPointer = false;
        let multipleFound = false;
        let overlayOffsetX = 0;
        let overlayOffsetY = 0;
        const features = map.getFeaturesAtPixel(e.pixel, {
          layerFilter: (l) => l instanceof VectorLayer,
        });
        if (features?.length) {
          // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
          const _features: FeatureLike[] = features.reduce<FeatureLike[]>(
            (tot, curr) => {
              return [
                ...tot,
                ...("features" in curr.getProperties()
                  ? (curr.getProperties().features as FeatureLike[])
                  : [curr]),
              ];
            },
            [],
          );
          const allFeatures = _features.sort(
            (a, b) =>
              (findLayerByFeatureId(
                map,
                (a.getProperties() as MapItem).mapId,
              )?.getZIndex() ?? 0) -
              (findLayerByFeatureId(
                map,
                (b.getProperties() as MapItem).mapId,
              )?.getZIndex() ?? 0),
          );
          if (allFeatures) {
            // Ignore features that have no type or no popups
            const filteredFeatures = allFeatures.filter((f) => {
              const type = f.get("type") as MapItem["type"];
              if (
                type === "port" &&
                focusedPort?.id === (f.getProperties() as PortMapItem).id
              ) {
                return false;
              }
              if (type in AllowedTypes) {
                // Pointer is set if an interactable entity is found
                // Regardless of event type
                setPointer =
                  setPointer ||
                  AllowedTypes[type]?.click ||
                  AllowedTypes[type]?.hover;
                return (
                  (e.type === "singleclick" && AllowedTypes[type]?.click) ||
                  (e.type === "pointermove" && AllowedTypes[type]?.hover)
                );
              }
              return false;
            });
            // setPointer = !!filteredFeatures.length;
            // Hover popup requires only 1 feature, click allows multiple
            if (filteredFeatures.length > 0) {
              const feat = filteredFeatures?.[0];
              item = feat?.getProperties() as MapItem & {
                geometry: Geometry;
              };

              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              overlayOffsetX = feat.get("overlayOffsetX") ?? 0;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              overlayOffsetY = feat.get("overlayOffsetY") ?? 0;
              overlay.set("featureCount", filteredFeatures.length);
              const { geometry } = item;
              if (geometry instanceof Point) {
                itemCoords = geometry.getCoordinates();
              } else {
                itemCoords = e.coordinate;
              }
              if (filteredFeatures.length > 1 && e.type === "singleclick") {
                multipleFound = true;
                setMultipleItems(
                  filteredFeatures?.map(
                    (f) =>
                      f?.getProperties() as MapItem & {
                        geometry: Geometry;
                      },
                  ),
                );
                itemCoords = e.coordinate;
              }
            }
          }
        }
        const vp = map.getViewport();
        if (setPointer && !disablePopups) {
          vp.style.cursor = "pointer";
        } else {
          vp.style.cursor = "";
        }

        if (itemCoords && item && !disablePopups) {
          if (
            e.type === "singleclick" &&
            AllowedTypes[item.type].click &&
            !disabledPopups?.[item.type]?.disableClick
          ) {
            if (multipleFound) {
              setSelectedItem(undefined);
            } else {
              setSelectedItem(item);
              setMultipleItems(undefined);
            }
            setHoveredItem(undefined);
            overlay?.setPosition(
              item.type === "measurement" ? e.coordinate : itemCoords,
            );
            overlay.setOffset([overlayOffsetX, overlayOffsetY]);
            overlay.set("clickedOpen", true);
          } else if (
            !overlay.get("clickedOpen") &&
            AllowedTypes[item.type].hover &&
            !disabledPopups?.[item.type]?.disableHover
          ) {
            setHoveredItem(item);
            overlay?.setPosition(
              item.type === "measurement" ? e.coordinate : itemCoords,
            );
            overlay.setOffset([overlayOffsetX, overlayOffsetY]);
            overlay.set("clickedOpen", false);
            setSelectedItem(undefined);
            setMultipleItems(undefined);
          }
        } else {
          if (e.type === "pointermove" && !overlay.get("clickedOpen")) {
            overlay?.setPosition(undefined);
            overlay.set("clickedOpen", false);
            setSelectedItem(undefined);
            setMultipleItems(undefined);
            setHoveredItem(undefined);
          }
          if (e.type === "singleclick") {
            overlay?.setPosition(undefined);
            overlay.set("clickedOpen", false);
            setSelectedItem(undefined);
            setHoveredItem(undefined);
            setMultipleItems(undefined);
          }
        }
      };

      // Add event listeners
      clickListenerKey.current = map.on("singleclick", listener);
      hoverListenerKey.current = map.on("pointermove", listener);
    },
    [disablePopups, disabledPopups, focusedPort?.id],
  );

  const unmountPopup = useCallback(() => {
    if (clickListenerKey.current) unByKey(clickListenerKey.current);
    if (hoverListenerKey.current) unByKey(hoverListenerKey.current);
  }, [clickListenerKey, hoverListenerKey]);

  return {
    createPopup,
    hoveredItem,
    selectedItem,
    multipleItems,
    unmountPopup,
  };
}

export default useMapPopup;
